import { isBefore } from 'date-fns'
import * as z from 'zod'

import raw from './requiredDistributions.json'

// The table only covers ages at which RMDs actually apply — ages below 72
// aren't stored (they'd have distributionPeriod=0, i.e. no RMD due).
const MIN_AGE = 72
const MAX_AGE = 131

// `distributionPercentage` is derived — computed once at parse time from
// `distributionPeriod` — so consumers don't have to divide on every call.
export const requiredDistributionSchema = z
  .object({
    age: z.int().min(MIN_AGE).max(MAX_AGE),
    distributionPeriod: z.number().positive(),
  })
  .transform(({ age, distributionPeriod }) => ({
    age,
    distributionPeriod,
    distributionPercentage: 100 / distributionPeriod,
  }))

export type RequiredDistribution = z.infer<typeof requiredDistributionSchema>

// Static IRS Uniform Lifetime Table (Table III, Publication 590-B).
// Validated at module load — any edit to the JSON that violates the
// schema throws before the calculator ever sees the data, instead of
// silently corrupting downstream RMD math.
export const REQUIRED_DISTRIBUTIONS: readonly RequiredDistribution[] = z
  .array(requiredDistributionSchema)
  .parse(raw)

export const DISTRIBUTION_BY_AGE: ReadonlyMap<number, RequiredDistribution> =
  new Map(REQUIRED_DISTRIBUTIONS.map(rd => [rd.age, rd]))

// SECURE Act / SECURE Act 2.0 cutoffs — the age at which required minimum
// distributions begin, keyed off the person's birthday.
export const getRequiredDistributionAge = (birthday: Date): number => {
  if (isBefore(birthday, new Date('1949-07-01'))) return 70.5
  if (isBefore(birthday, new Date('1951-01-01'))) return 72
  if (isBefore(birthday, new Date('1960-01-01'))) return 73
  return 75
}
